import React, { createContext, useContext, useState, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { apiConfig, loginRequest } from '../authConfig';

/**
 * 认证全局 Context
 * 用于在所有组件中共享 session 过期状态和 token 获取
 */
const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const { instance, accounts } = useMsal();
  const [sessionExpired, setSessionExpired] = useState(false);

  /**
   * 获取 Access Token
   * 自动处理静默获取失败和 session 过期的情况
   */
  const getAccessToken = useCallback(async (options = {}) => {
    const { 
      scopes = apiConfig.scopes,
      forceRefresh = false,
    } = options;

    try {
      if (accounts.length === 0) {
        console.error('[AuthContext] No accounts found');
        throw new Error('No authenticated user');
      }

      console.log('[AuthContext] Attempting to acquire token silently...');
      
      // 尝试静默获取 token
      const response = await instance.acquireTokenSilent({
        scopes,
        account: accounts[0],
        forceRefresh,
      });
      
      console.log('[AuthContext] Token acquired successfully');
      return response.accessToken;
      
    } catch (error) {
      console.warn('[AuthContext] Silent token acquisition failed:', error.message);
      
      // 检查是否是需要交互或超时错误
      const isInteractionRequired = 
        error instanceof InteractionRequiredAuthError ||
        error.name === 'BrowserAuthError' ||
        error.message?.includes('timeout') ||
        error.message?.includes('interaction_required') ||
        error.message?.includes('consent_required') ||
        error.message?.includes('login_required');

      if (isInteractionRequired) {
        console.log('[AuthContext] Session expired or interaction required - triggering global session expired state');
        
        // 设置全局 session 过期状态 - 所有组件都能看到
        setSessionExpired(true);
        
        throw new Error('SESSION_EXPIRED');
      }
      
      // 其他错误直接抛出
      throw error;
    }
  }, [instance, accounts]);

  /**
   * 清除 MSAL 缓存并直接触发重新登录
   * @param {string} redirectPath - 登录成功后要返回的页面路径
   */
  const handleLogout = useCallback(async (redirectPath = '/') => {
    try {
      console.log('[AuthContext] Clearing MSAL cache and triggering re-login...');
      
      // 清除 sessionStorage 中的 MSAL 交互状态
      sessionStorage.clear();
      
      // 清除 localStorage 中的 MSAL 缓存
      const msalKeys = Object.keys(localStorage).filter(key =>
        key.startsWith('msal.') || key.startsWith('@@msal@@')
      );
      msalKeys.forEach(key => {
        console.log('[AuthContext] Clearing key:', key);
        localStorage.removeItem(key);
      });
      
      console.log('[AuthContext] All MSAL cache cleared, starting loginRedirect...');

      // 直接触发登录，用 MSAL state 参数携带原页面路径
      await instance.loginRedirect({
        ...loginRequest,
        state: redirectPath && redirectPath !== '/' ? redirectPath : undefined,
        prompt: 'select_account',
      });
    } catch (error) {
      console.error('[AuthContext] Error during re-login:', error);
      window.location.href = '/';
    }
  }, [instance]);

  /**
   * 重置 session 过期状态
   */
  const resetSessionExpired = useCallback(() => {
    setSessionExpired(false);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        getAccessToken,
        handleLogout,
        sessionExpired,
        resetSessionExpired,
        accounts,
        isAuthenticated: accounts.length > 0,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Hook 来使用认证 context
 */
export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within AuthProvider');
  }
  return context;
};
